import React from 'react';
import { Calendar } from 'lucide-react';
import {
  computePricePair,
  effectiveOptionPriceTTC,
  formatTtcPreview,
  parseQuestionRequired,
  parseTextKind,
  questionAddsValueToTotalTTC,
} from '../../utils/hubupNotificationForm';

const editableFieldClass =
  'w-full min-w-0 max-w-full box-border rounded-2xl border border-[#DDD6FE] px-3 py-2.5 text-sm text-[#1E1B4B] bg-white placeholder:text-[#9CA3AF]';

function formatOptionPriceTTC(opt) {
  const fromApi = effectiveOptionPriceTTC(opt);
  if (fromApi != null) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(fromApi);
  }
  if (opt?.monetize === true && String(opt?.priceAmount ?? '').trim()) {
    return formatTtcPreview(opt.priceAmount, opt.priceVatRate);
  }
  return null;
}

function textPlaceholder(kind, addsToTotal) {
  if (addsToTotal) return '0,00';
  if (kind === 'number') return '0';
  if (kind === 'email') return 'exemple@domaine.com';
  return 'Votre réponse…';
}

/**
 * Aperçu statique du formulaire (style hubupfront FormFillContent).
 */
export default function HubupNotificationFormPreview({ form }) {
  const name = String(form?.name ?? '').trim();
  const description = String(form?.description ?? '').trim();
  const questions = Array.isArray(form?.questions) ? form.questions : [];

  return (
    <div className="mx-auto min-w-0 max-w-lg pointer-events-none select-none">
      <div className="min-w-0 overflow-x-hidden rounded-3xl border border-[#E8E4FF] bg-white p-5 shadow-sm space-y-5">
        <div>
          <h2 className="text-lg font-bold text-[#1E1B4B]">
            {name || (
              <span className="text-[#9CA3AF] font-normal italic">Nom du formulaire</span>
            )}
          </h2>
          {description ? (
            <p className="mt-2 text-sm text-[#333483] leading-relaxed whitespace-pre-wrap">
              {description}
            </p>
          ) : (
            <p className="mt-2 text-sm text-[#9CA3AF] italic">Description (optionnelle)</p>
          )}
        </div>

        {!questions.length ? (
          <p className="text-sm text-[#6B7280] italic text-center py-6">
            Ajoutez des champs pour les voir ici.
          </p>
        ) : (
          <div className="min-w-0 space-y-6">
            {questions.map((q, qIndex) => {
              const required = parseQuestionRequired(q?.required);
              const textKind = parseTextKind(q?.textKind);
              const addsToTotal = questionAddsValueToTotalTTC(q);
              const label =
                String(q?.questionName ?? '').trim() ||
                `Champ ${qIndex + 1}`;
              const maxSel = Math.max(1, Number(q?.optionsSelectable) || 1);
              const options = (Array.isArray(q?.options) ? q.options : []).filter(
                (o) => String(o?.optionName ?? '').trim() || q.type === 'options',
              );

              return (
                <fieldset key={qIndex} className="min-w-0 space-y-2.5" disabled>
                  <legend className="text-sm font-bold text-[#1E1B4B]">
                    {label}
                    {required ? (
                      <span className="text-[#B91C1C] ml-0.5" aria-hidden>
                        *
                      </span>
                    ) : (
                      <span className="ml-1 text-xs font-normal text-[#6B7280]">
                        (optionnel)
                      </span>
                    )}
                    {q.type === 'options' && maxSel > 1 ? (
                      <span className="ml-1 text-xs font-normal text-[#333483]">
                        (max {maxSel})
                      </span>
                    ) : null}
                  </legend>

                  {q.type === 'note' ? (
                    <div
                      className="flex gap-1.5 text-[#F59E0B]"
                      aria-hidden
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span key={star} className="text-xl leading-none opacity-40">
                          ★
                        </span>
                      ))}
                    </div>
                  ) : q.type === 'text' ? (
                    textKind === 'long' ? (
                      <textarea
                        readOnly
                        rows={4}
                        className={editableFieldClass}
                        placeholder={textPlaceholder(textKind, addsToTotal)}
                        defaultValue=""
                      />
                    ) : textKind === 'date' ? (
                      <div className="flex w-full min-w-0 items-stretch overflow-hidden rounded-2xl border border-[#DDD6FE] bg-white">
                        <input
                          type="text"
                          readOnly
                          className="flex-1 border-0 bg-transparent px-3 py-2.5 text-sm text-[#9CA3AF]"
                          placeholder="jj/mm/aaaa"
                        />
                        <span className="flex w-11 shrink-0 items-center justify-center border-l border-[#F0EDFF] text-[#333483]">
                          <Calendar size={18} aria-hidden />
                        </span>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          readOnly
                          className={editableFieldClass}
                          placeholder={textPlaceholder(textKind, addsToTotal)}
                        />
                        {addsToTotal ? (
                          <p className="text-[11px] text-[#333483]">
                            Montant en € TTC — ajouté au total à payer.
                          </p>
                        ) : null}
                      </>
                    )
                  ) : q.type === 'options' ? (
                    <div className="space-y-2">
                      {options.length ? (
                        options.map((opt, optIndex) => {
                          const optLabel =
                            String(opt?.optionName ?? '').trim() ||
                            `Option ${optIndex + 1}`;
                          const priceStr = formatOptionPriceTTC(opt);
                          const inputType = maxSel === 1 ? 'radio' : 'checkbox';
                          return (
                            <label
                              key={optIndex}
                              className="flex items-start gap-3 rounded-2xl border border-[#E8E4FF] bg-white px-3 py-2.5"
                            >
                              <input
                                type={inputType}
                                name={`preview-q-${qIndex}`}
                                readOnly
                                className="mt-1 shrink-0 accent-[#010064]"
                              />
                              <span className="min-w-0 flex-1 text-sm text-[#333483]">
                                {optLabel}
                                {priceStr ? (
                                  <span className="ml-1 font-mono text-xs text-[#010064]">
                                    (+{priceStr})
                                  </span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <p className="text-sm text-[#6B7280] italic">Aucune option</p>
                      )}
                    </div>
                  ) : null}
                </fieldset>
              );
            })}
          </div>
        )}

        <button
          type="button"
          tabIndex={-1}
          className="w-full rounded-2xl bg-gradient-to-r from-[#010064] to-[#333483] px-4 py-3 text-sm font-bold text-white shadow-md opacity-50"
        >
          {(() => {
            const base = computePricePair(form?.priceAmount, form?.priceVatRate);
            const hasBase = String(form?.priceAmount ?? '').trim() !== '';
            if (hasBase && base.priceTTC != null) {
              const p = formatTtcPreview(form.priceAmount, form.priceVatRate);
              return p ? `Soumettre et payer (${p})` : 'Soumettre votre réponse';
            }
            return 'Soumettre votre réponse';
          })()}
        </button>
      </div>
    </div>
  );
}
